import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { ColumnMapping } from './PublisherBulkUploadModal';

interface PublisherColumnMappingStepProps {
  csvColumns: string[];
  autoMappings: ColumnMapping[];
  onComplete: (mappings: ColumnMapping[]) => void;
  onBack: () => void;
  loading: boolean;
}

const dbFields = [
  { value: '', label: 'Skip this column' },
  { value: 'publisherName', label: 'Publisher Name *', required: true },
  { value: 'email', label: 'Email *', required: true },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'modeOfCommunication', label: 'Mode of Communication' },
  { value: 'isActive', label: 'Active Status' },
];

const PublisherColumnMappingStep: React.FC<PublisherColumnMappingStepProps> = ({
  csvColumns,
  autoMappings,
  onComplete,
  onBack,
  loading,
}) => {
  const [mappings, setMappings] = useState<ColumnMapping[]>(autoMappings);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    setMappings(autoMappings);
  }, [autoMappings]);

  const handleMappingChange = (csvColumn: string, dbField: string) => {
    setMappings(prev =>
      prev.map(m =>
        m.csvColumn === csvColumn ? { ...m, dbField } : m
      )
    );
    setError('');
  };

  const validateMappings = (): boolean => {
    const mappedFields = mappings
      .filter(m => m.dbField)
      .map(m => m.dbField);

    // Check required fields
    const requiredFields = ['publisherName', 'email'];
    const missingFields = requiredFields.filter(
      field => !mappedFields.includes(field)
    );

    if (missingFields.length > 0) {
      setError(`Missing required field mappings: ${missingFields.join(', ')}`);
      return false;
    }

    // Check for duplicate mappings
    const duplicates = mappedFields.filter(
      (field, index) => field && mappedFields.indexOf(field) !== index
    );

    if (duplicates.length > 0) {
      setError(`Duplicate field mappings detected: ${duplicates.join(', ')}`);
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (validateMappings()) {
      onComplete(mappings);
    }
  };

  const getUsedFields = () => {
    return mappings
      .filter(m => m.dbField)
      .map(m => m.dbField);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        Map your CSV columns to the corresponding database fields. Fields marked with * are required.
      </Alert>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>CSV Column</strong></TableCell>
              <TableCell><strong>Sample Data</strong></TableCell>
              <TableCell><strong>Map to Database Field</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((mapping, index) => {
              const usedFields = getUsedFields();
              return (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {mapping.csvColumn}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {/* Sample data would be shown here if available */}
                      -
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        value={mapping.dbField}
                        onChange={(e) => handleMappingChange(mapping.csvColumn, e.target.value)}
                        displayEmpty
                      >
                        {dbFields.map((field) => (
                          <MenuItem
                            key={field.value}
                            value={field.value}
                            disabled={
                              field.value !== '' &&
                              field.value !== mapping.dbField &&
                              usedFields.includes(field.value)
                            }
                          >
                            {field.label}
                            {field.value !== '' &&
                              field.value !== mapping.dbField &&
                              usedFields.includes(field.value) &&
                              ' (already mapped)'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button onClick={onBack} disabled={loading}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={loading}
        >
          Next: Preview Data
        </Button>
      </Box>
    </Box>
  );
};

export default PublisherColumnMappingStep;
